
import React, { useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';

function formatCurrency(value, locale, currency) {
  if (value === undefined || value === null) return '—';
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0
    }).format(value);
  } catch {
    return Number(value).toLocaleString();
  }
}

function formatNumber(value, locale, digits = 2) {
  if (value === undefined || value === null) return '—';
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits
  }).format(value);
}

function formatPercent(value, locale, digits = 2) {
  if (value === undefined || value === null) return '—';
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: digits
  }).format(value);
}

/** Converte o atributo `date` (year) para número seguro. */
function toYear(value) {
  if (value === undefined || value === null) return NaN;
  const n = typeof value === 'number' ? value : parseInt(String(value), 10);
  return Number.isNaN(n) ? NaN : n;
}

/**
 * Props:
 * - data: Array de objetos:
 *   { date: number|string (Year), revenue, netIncome, incomeTaxExpense, marginProfit, epsStatement,
 *     cashAndEquivalents, ebitda, totalEquity, netDebt, shortTermDebt,
 *     netDebtToEbitda?, netDebtRatio? }
 * - currency: 'AUD' | 'USD' | ...
 * - locale: 'pt-BR' | 'en-AU' | ...
 * - marginIsPercentage: boolean (default true)
 * - sortDesc: boolean (true = ordem decrescente por Year), default true
 */
export default function FinancialMetricsTable({
  data = [],
  currency = 'AUD',
  locale = 'en-AU',
  marginIsPercentage = true,
  sortDesc = true,
}) {

    
// Converte qualquer campo de ano (number ou string) para número
const toYear = (value) => {
  if (value === undefined || value === null) return NaN;
  const n = typeof value === 'number' ? value : parseInt(String(value).trim(), 10);
  return Number.isNaN(n) ? NaN : n;
};

// Extrai o ano do objeto (prioriza Year, depois calendarYear_x e calendarYear_y)
const getYear = (row) => {
  return toYear(
    row?.Year ?? row?.year ?? row?.calendarYear_x ?? row?.calendarYear_y
  );
};

// DESC por Year (ex.: 2025 -> 2006)
const sortedData = React.useMemo(() => {
  const copy = Array.isArray(data) ? [...data] : [];
  copy.sort((a, b) => {
    const ya = getYear(a);
    const yb = getYear(b);

    // Empurra valores inválidos (NaN) para o fim
    if (Number.isNaN(ya) && Number.isNaN(yb)) return 0;
    if (Number.isNaN(ya)) return 1;
    if (Number.isNaN(yb)) return -1;

    return yb - ya; // DESC
  });
  return copy;
}, [data]);


  const computeNetDebtToEbitda = (row) => {
    if (row.netDebtToEbitda !== undefined && row.netDebtToEbitda !== null) return row.netDebtToEbitda;
    const netDebt = row.netDebt ?? 0;
    const ebitda = row.ebitda ?? 0;
    if (!ebitda) return undefined;
    return netDebt / ebitda;
  };

  const computeNetDebtRatio = (row) => {
    if (row.netDebtRatio !== undefined && row.netDebtRatio !== null) return row.netDebtRatio;
    const netDebt = row.netDebt ?? 0;
    const equity = row.totalEquity ?? 0;
    if (!equity) return undefined;
    return netDebt / equity;
  };

  const renderMargin = (row) => {
    if (row.netIncomeRatio === undefined || row.netIncomeRatio === null) return '—';
    return marginIsPercentage
      ? formatPercent(row.netIncomeRatio, locale, 1)   // 0.35 => 35%
      : formatCurrency(row.netIncomeRatio, locale, currency); // valor absoluto
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Financial Results
      </Typography>

      <TableContainer component={Paper} sx={{ maxHeight: 1000 }}>
        <Table stickyHeader size="small" aria-label="financial-metrics-table">
          <TableHead>
            <TableRow>
              <TableCell>Year</TableCell>
              <TableCell align="right">Revenue</TableCell>
              <TableCell align="right">Net Income</TableCell>
              <TableCell align="right">Income Tax Expense</TableCell>
              <TableCell align="right">Margim Profit</TableCell>
              <TableCell align="right">EPS Statement</TableCell>
              <TableCell align="right">Cash and Equivalents</TableCell>
              <TableCell align="right">EBITDA</TableCell>
              <TableCell align="right">Total Equity</TableCell>
              <TableCell align="right">Net Debt</TableCell>
              <TableCell align="right">Short Term Debt</TableCell>
              <TableCell align="right">Net Debt / EBITDA</TableCell>
              <TableCell align="right">Net Debt Ratio (Debt/Total Equity)</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {sortedData?.length ? (
              sortedData.map((row, idx) => {
                const ndToEbitda = computeNetDebtToEbitda(row);
                const ndRatio = computeNetDebtRatio(row);
                const year = toYear(row.date);

                return (
                  <TableRow key={idx} hover>
                    
                    <TableCell>
                    {(() => {
                        const y = getYear(row);
                        return Number.isNaN(y) ? '—' : y;
                    })()}
                    </TableCell>

                    <TableCell align="right">{formatCurrency(row.revenue, locale, currency)}</TableCell>
                    <TableCell align="right">{formatCurrency(row.netIncome, locale, currency)}</TableCell>
                    <TableCell align="right">{formatCurrency(row.incomeTaxExpense, locale, currency)}</TableCell>
                    <TableCell align="right">{renderMargin(row)}</TableCell>
                    <TableCell align="right">{formatNumber(row.eps, locale, 3)}</TableCell>
                    <TableCell align="right">{formatCurrency(row.cashAndCashEquivalents, locale, currency)}</TableCell>
                    <TableCell align="right">{formatCurrency(row.ebitda, locale, currency)}</TableCell>
                    <TableCell align="right">{formatCurrency(row.totalEquity, locale, currency)}</TableCell>
                    <TableCell align="right">{formatCurrency(row.netDebt, locale, currency)}</TableCell>
                    <TableCell align="right">{formatCurrency(row.shortTermDebt, locale, currency)}</TableCell>
                    <TableCell align="right">
                      {ndToEbitda === undefined ? '—' : formatNumber(ndToEbitda, locale, 2)}
                    </TableCell>
                    <TableCell align="right">
                      {ndRatio === undefined ? '—' : formatNumber(ndRatio, locale, 2)}
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={13}>
                  <Typography variant="body2" color="text.secondary">
                    No data available.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
